import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./sentry";
// Session transport, imported for its side effects and imported HERE (7HAI-923).
// ApiClient installs the axios interceptor and the fetch wrapper that put the
// session token in an `Authorization: Bearer` header instead of the URL, and it
// sets OpenAPI.BASE. Its only importer used to be LoginPage — which is
// lazy-loaded, so a user who reloaded straight onto a protected route with a
// valid session never fetched that chunk, and every request from that tab went
// out with the token in the query string and no Authorization header at all.
// That is how live tokens kept reaching the access log after 7HAI-929 removed
// them from the published schema. The entry point is the only place that can
// guarantee the transport is installed before the first request.
import "./api/ApiClient";
import App from "./App";
import AppErrorFallback from "./components/AppErrorFallback";
import { installSwUpdateReload } from "./utils/swUpdateReload";
import "./i18n";
import "./global.css";
import "./components/ui/buttons/Buttons.css";

// Dev-only accessibility auditing (stripped from production builds).
if (import.meta.env.DEV) {
  void import("@axe-core/react").then(({ default: axe }) => {
    axe(React, ReactDOM, 1000);
  });
}

// 7HAI-1253: a deploy replaces the service worker, and this one is generated
// with skipWaiting + clientsClaim + cleanupOutdatedCaches. It therefore seizes
// THIS page — whose HTML and entry bundle are the previous build — and deletes
// the precache those chunks live in. Nothing then replaced the document, so the
// page kept running code the worker underneath it had already disowned, and the
// next lazy import (logging in, for most people) failed.
//
// Installed before render so the swap is caught however early it lands.
// lazyWithRetry stays as the backstop for a chunk that 404s with no swap.
installSwUpdateReload();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    {/* 7HAI-1224: the fallback used to be a bare paragraph with no reload and
        no way back, so whatever crashed, the only exit was editing the URL by
        hand. It now offers a reload and names a stale build for what it is.
        (Phrased without quoting the old JSX: the literal scanner reads text
        inside JSX comments and would count the quotation as a hardcoded
        string.) */}
    <Sentry.ErrorBoundary
      fallback={({ error, eventId }) => <AppErrorFallback error={error} eventId={eventId} />}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
